import React from "react";
import { Play, Trash, Broadcast, Copy, PaperPlaneTilt, MapPin, MapTrifold, UserPlus, Receipt, PencilSimple } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { TYPE_META } from "./destinationConstants";
import { targetDisplay } from "./destinationHelpers";

/**
 * Presentational table of configured destinations. Callbacks supplied
 * by the parent. Iter-60: renders a wide `<table>` on desktop and a
 * stack of mobile cards on phones (<768px). Iter-61.4.1: buyer name
 * is now read directly from `r.client_name` (populated server-side)
 * instead of a preloaded `clients` array capped at 100.
 */
export default function DestinationTable({
  rows,
  onToggle, onTest, onDelete, onClone, onQuickClone, onBulkClone, onSendDigest,
  onInviteBuyer, onInvoicePreview, onEdit,
}) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  return (
    <div className="brut-card">
      <div className="hidden md:block overflow-x-auto">
      <table className="w-full text-sm font-mono min-w-[1000px]">
        <thead>
          <tr className="border-b border-black text-[10px] uppercase tracking-widest">
            <th className="py-2 px-4 text-left">Name</th>
            <th className="py-2 px-4 text-left">Client</th>
            <th className="py-2 px-4 text-left">Type</th>
            <th className="py-2 px-4 text-left">Target</th>
            <th className="py-2 px-4 text-left">Campaigns</th>
            <th className="py-2 px-4 text-left">Retries</th>
            <th className="py-2 px-4 text-left">Digest</th>
            <th className="py-2 px-4 text-left">Active</th>
            <th className="py-2 px-4 text-left"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const meta = TYPE_META[r.type] || { label: r.type, Icon: Broadcast };
            const Icon = meta.Icon;
            const clientName = r.client_name;
            const target = targetDisplay(r);
            const digestOn = !!(r.config?.daily_digest_enabled);
            const canQuickClone = r.type === "email" && Array.isArray(r.config?.state_allowlist)
                                  && r.config.state_allowlist.length > 0;
            const isPostpayWithInvoice = r.config?.payment_mode === "postpay" && !!r.config?.invoice_enabled;
            return (
              <tr key={r.id} className="border-b border-zinc-200" data-testid={`dest-row-${r.id}`}>
                <td className="py-2 px-4 font-semibold">{r.name}</td>
                <td className="py-2 px-4 text-[11px]">
                  {r.client_id ? (clientName || r.client_id.slice(0, 8)) : (
                    <span className="text-amber-700 uppercase tracking-widest">Platform</span>
                  )}
                </td>
                <td className="py-2 px-4">
                  <span className="inline-flex items-center gap-1">
                    <Icon size={12} /> {meta.label}
                  </span>
                </td>
                <td className="py-2 px-4 text-[11px] truncate max-w-[220px]" title={target}>
                  {target}
                </td>
                <td className="py-2 px-4 text-[11px]">
                  {(r.campaign_ids || []).length === 0 ? "all" : (r.campaign_ids || []).length}
                </td>
                <td className="py-2 px-4">{r.retry_policy?.max_attempts || 5}</td>
                <td className="py-2 px-4" data-testid={`dest-digest-${r.id}`}>
                  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest border ${digestOn ? "border-emerald-700 bg-emerald-50 text-emerald-800" : "border-zinc-300 text-zinc-400"}`}>
                    {digestOn ? "on" : "off"}
                  </span>
                </td>
                <td className="py-2 px-4">
                  <button onClick={() => onToggle(r)} data-testid={`dest-toggle-${r.id}`}
                    className={`px-2 py-0.5 text-[10px] uppercase tracking-widest border ${r.is_active ? "border-black bg-black text-white" : "border-zinc-500 text-zinc-500"}`}>
                    {r.is_active ? "on" : "off"}
                  </button>
                </td>
                <td className="py-2 px-4 whitespace-nowrap">
                  <button onClick={() => onTest(r.id)} data-testid={`dest-test-${r.id}`}
                    className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white mr-1">
                    <Play size={10} className="inline" /> Test
                  </button>
                  {onSendDigest && digestOn && (
                    <button onClick={() => onSendDigest(r)} data-testid={`dest-send-digest-${r.id}`}
                      title="Send today's digest to this buyer now"
                      className="border border-emerald-700 text-emerald-800 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-emerald-700 hover:text-white mr-1">
                      <PaperPlaneTilt size={10} className="inline" /> Send Now
                    </button>
                  )}
                  {onQuickClone && canQuickClone && (
                    <button onClick={() => onQuickClone(r)} data-testid={`dest-quick-clone-${r.id}`}
                      title="Clone this destination for another state — asks only for state + email"
                      className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white mr-1">
                      <MapPin size={10} className="inline" /> New State
                    </button>
                  )}
                  {onBulkClone && canQuickClone && (
                    <button onClick={() => onBulkClone(r)} data-testid={`dest-bulk-clone-${r.id}`}
                      title="Bulk clone — paste a CSV of STATE, FEE rows to onboard a whole region as ONE destination"
                      className="border border-indigo-700 text-indigo-800 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-indigo-700 hover:text-white mr-1">
                      <MapTrifold size={10} className="inline" /> Bulk Region
                    </button>
                  )}
                  {onInviteBuyer && r.type === "email" && (
                    <button onClick={() => onInviteBuyer(r)} data-testid={`dest-invite-buyer-${r.id}`}
                      title="Invite a buyer login for this destination (read-only dashboard)"
                      className="border border-sky-700 text-sky-800 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-sky-700 hover:text-white mr-1">
                      <UserPlus size={10} className="inline" /> Buyer
                    </button>
                  )}
                  {onInvoicePreview && isPostpayWithInvoice && (
                    <button onClick={() => onInvoicePreview(r)} data-testid={`dest-invoice-${r.id}`}
                      title="Preview or send this destination's weekly invoice"
                      className="border border-emerald-800 text-emerald-900 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-emerald-800 hover:text-white mr-1">
                      <Receipt size={10} className="inline" /> Invoice
                    </button>
                  )}
                  {onEdit && (
                    <button onClick={() => onEdit(r)} data-testid={`dest-edit-${r.id}`}
                      title="Edit this destination — pre-fills the form with current settings"
                      className="border border-amber-700 text-amber-800 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-amber-700 hover:text-white mr-1">
                      <PencilSimple size={10} className="inline" /> Edit
                    </button>
                  )}
                  {onClone && (
                    <button onClick={() => onClone(r)} data-testid={`dest-clone-${r.id}`}
                      className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white mr-1">
                      <Copy size={10} className="inline" /> Clone
                    </button>
                  )}
                  {isSuperAdmin && (
                    <button onClick={() => onDelete(r.id)} data-testid={`dest-delete-${r.id}`}
                      className="border border-red-600 text-red-700 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-red-600 hover:text-white">
                      <Trash size={10} className="inline" /> Delete
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
          {!rows.length && <tr><td colSpan={9} className="py-6 text-center label-mono">No destinations configured</td></tr>}
        </tbody>
      </table>
      </div>
      {/* Mobile card view. Preserves every button + status the desktop row shows. */}
      <div className="md:hidden p-2 flex flex-col gap-2" data-testid="dest-cards">
        {rows.map((r) => {
          const meta = TYPE_META[r.type] || { label: r.type, Icon: Broadcast };
          const Icon = meta.Icon;
          const clientName = r.client_name;
          const target = targetDisplay(r);
          const digestOn = !!(r.config?.daily_digest_enabled);
          const canQuickClone = r.type === "email" && Array.isArray(r.config?.state_allowlist)
                                && r.config.state_allowlist.length > 0;
          const isPostpayWithInvoice = r.config?.payment_mode === "postpay" && !!r.config?.invoice_enabled;
          return (
            <div key={r.id} className="border border-black bg-white p-3" data-testid={`dest-card-${r.id}`}>
              <div className="flex items-center gap-2 mb-1 pb-1 border-b border-zinc-200">
                <div className="font-bold text-sm truncate flex-1">{r.name}</div>
                <button onClick={() => onToggle(r)} data-testid={`dest-toggle-mobile-${r.id}`}
                  className={`px-2 py-0.5 text-[10px] uppercase tracking-widest border ${r.is_active ? "border-black bg-black text-white" : "border-zinc-500 text-zinc-500"}`}>
                  {r.is_active ? "on" : "off"}
                </button>
              </div>
              <div className="flex items-center gap-2 text-xs mt-1">
                <Icon size={12} /> <span>{meta.label}</span>
                <span className="text-zinc-400">·</span>
                <span className="truncate">{r.client_id ? (clientName || r.client_id.slice(0, 8)) : "Platform"}</span>
              </div>
              <div className="text-[11px] text-zinc-600 truncate mt-1" title={target}>{target}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest mt-1 text-zinc-500">
                Campaigns {(r.campaign_ids || []).length === 0 ? "all" : (r.campaign_ids || []).length} · retries {r.retry_policy?.max_attempts || 5} · digest {digestOn ? "on" : "off"}
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                <button onClick={() => onTest(r.id)} data-testid={`dest-test-mobile-${r.id}`}
                  className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white">
                  <Play size={10} className="inline" /> Test
                </button>
                {onSendDigest && digestOn && (
                  <button onClick={() => onSendDigest(r)} data-testid={`dest-send-digest-mobile-${r.id}`}
                    className="border border-emerald-700 text-emerald-800 px-2 py-1 text-[10px] uppercase tracking-widest">
                    <PaperPlaneTilt size={10} className="inline" /> Send
                  </button>
                )}
                {onQuickClone && canQuickClone && (
                  <button onClick={() => onQuickClone(r)} data-testid={`dest-quick-clone-mobile-${r.id}`}
                    className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest">
                    <MapPin size={10} className="inline" /> State
                  </button>
                )}
                {onEdit && (
                  <button onClick={() => onEdit(r)} data-testid={`dest-edit-mobile-${r.id}`}
                    className="border border-amber-700 text-amber-800 px-2 py-1 text-[10px] uppercase tracking-widest">
                    <PencilSimple size={10} className="inline" /> Edit
                  </button>
                )}
                {onClone && (
                  <button onClick={() => onClone(r)} data-testid={`dest-clone-mobile-${r.id}`}
                    className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest">
                    <Copy size={10} className="inline" /> Clone
                  </button>
                )}
                {onInvoicePreview && isPostpayWithInvoice && (
                  <button onClick={() => onInvoicePreview(r)} data-testid={`dest-invoice-mobile-${r.id}`}
                    className="border border-emerald-800 text-emerald-900 px-2 py-1 text-[10px] uppercase tracking-widest">
                    <Receipt size={10} className="inline" /> Invoice
                  </button>
                )}
                {isSuperAdmin && (
                  <button onClick={() => onDelete(r.id)} data-testid={`dest-delete-mobile-${r.id}`}
                    className="border border-red-600 text-red-700 px-2 py-1 text-[10px] uppercase tracking-widest">
                    <Trash size={10} className="inline" /> Delete
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {!rows.length && <div className="py-6 text-center label-mono">No destinations configured</div>}
      </div>
    </div>
  );
}
